import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import SvgIcon from "@material-ui/core/SvgIcon";
import Button from "components/CustomButtons/Button";
import Tooltip from "@material-ui/core/Tooltip";
import EmailIcon from "@material-ui/icons/Email";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  card: {
    marginBottom: 12,
    position: "relative",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: "1px solid #e0e0e0",
  },
  icon: {
    marginRight: 12,
    fontSize: 32,
  },
  assessmentName: {
    fontWeight: 600,
    fontSize: 16,
    color: "#333",
    flex: 1,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 8,
    fontSize: 14,
  },
  label: {
    fontWeight: 600,
    color: "#666",
  },
  value: {
    color: "#333",
  },
  groupBadge: {
    borderRadius: 10,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 4,
    paddingBottom: 4,
    background: "#40628d",
    fontSize: 12,
    fontWeight: 400,
    color: "#f7f7f7",
    display: "inline-block",
  },
  pendingBadge: {
    fontWeight: 900,
    color: "#c9423a",
    border: "1px dashed #c9423a",
    fontSize: 14,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
    borderRadius: 20,
    display: "inline-block",
  },
  cardActions: {
    padding: "8px 16px",
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: "#fafafa",
    borderTop: "1px solid #e0e0e0",
  },
  actionButton: {
    flex: 1,
    marginRight: 8,
    "&:last-child": {
      marginRight: 0,
    },
  },
  emailButton: {
    minWidth: 48,
  },
});

interface AssignedAssessment {
  accessor?: React.ReactNode;
  completed?: boolean;
  completed_date?: string;
  date?: string;
  group?: React.ReactNode;
  lms?: boolean;
  name?: React.ReactNode;
}

interface AssignedAssessmentCardProps {
  assessment: AssignedAssessment;
  icon: React.ElementType;
  color?: string;
  onSendEmail?: (accessor?: React.ReactNode) => void;
  onViewReport?: (accessor?: React.ReactNode) => void;
}

export default function AssignedAssessmentCard({
  assessment,
  icon,
  color,
  onSendEmail,
  onViewReport,
}: AssignedAssessmentCardProps): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent>
        {/* Header with icon and name */}
        <div className={classes.header}>
          <SvgIcon
            component={icon}
            className={classes.icon}
            style={{ color: color }}
          />
          <Typography className={classes.assessmentName}>
            {assessment.name}
          </Typography>
        </div>

        {/* Assigned Date */}
        <div className={classes.infoRow}>
          <span className={classes.label}>
            {t("assignments:allAssigned.tableHeaders.assignedDate")}:
          </span>
          <span className={classes.value}>
            {dayjs(assessment.date).format("DD/MM/YYYY")}
          </span>
        </div>

        {/* Completed Date */}
        <div className={classes.infoRow}>
          <span className={classes.label}>
            {t("assignments:allAssigned.tableHeaders.completedDate")}:
          </span>
          <span className={classes.value}>
            {assessment.completed
              ? dayjs(assessment.completed_date).format("DD/MM/YYYY")
              : t("assignments:allAssigned.pending")}
          </span>
        </div>

        {/* Group */}
        <div className={classes.infoRow}>
          <span className={classes.label}>
            {t("assignments:allAssigned.tableHeaders.assignedToGroup")}:
          </span>
          <span className={classes.value}>
            {assessment.group ? (
              <span className={classes.groupBadge}>{assessment.group}</span>
            ) : (
              t("assignments:allAssigned.no")
            )}
          </span>
        </div>
      </CardContent>

      <CardActions className={classes.cardActions}>
        {/* View Report or Pending */}
        <div className={classes.actionButton}>
          {assessment.completed ? (
            <Button
              fullWidth
              size="sm"
              color="success"
              onClick={() => onViewReport!(assessment.accessor)}
            >
              {t("assignments:allAssigned.viewReport")}
            </Button>
          ) : (
            <div style={{ textAlign: "center" }}>
              <span className={classes.pendingBadge}>
                {t("assignments:allAssigned.pending")}
              </span>
            </div>
          )}
        </div>

        {/* Send Email Button */}
        <Tooltip
          title={
            <Typography style={{ fontSize: 14 }}>
              {assessment.completed
                ? t("assignments:allAssigned.sendReport")
                : assessment.lms
                ? t("assignments:allAssigned.sendReminderShort")
                : t("assignments:allAssigned.lmsActivationReminder")}
            </Typography>
          }
          placement="top"
        >
          <div className={classes.emailButton}>
            <Button
              justIcon
              round
              simple
              color="info"
              onClick={() => onSendEmail!()}
            >
              <EmailIcon />
            </Button>
          </div>
        </Tooltip>
      </CardActions>
    </Card>
  );
}
